<template>
  <div class="youtube-transcript-page">
    <div class="container">
      <div class="header">
        <h1>YouTube Video Summarizer</h1>
        <p>Get AI-powered summaries of YouTube videos based on their transcripts</p>
      </div>

      <div class="input-section">
        <el-card>
          <div slot="header" class="clearfix">
            <span>Enter YouTube URL</span>
          </div>
          <el-input
            v-model="youtubeUrl"
            placeholder="https://www.youtube.com/watch?v=..."
            size="large"
            clearable
            @keyup.enter="handleSubmit"
          >
            <el-button slot="append" icon="el-icon-search" @click="handleSubmit" :loading="loading">
              Analyze
            </el-button>
          </el-input>
          <div class="url-examples">
            <p>Supported formats:</p>
            <ul>
              <li>https://www.youtube.com/watch?v=VIDEO_ID</li>
              <li>https://youtu.be/VIDEO_ID</li>
              <li>https://m.youtube.com/watch?v=VIDEO_ID</li>
            </ul>
            <el-alert
              title="Note"
              type="info"
              :closable="false"
              style="margin-top: 10px;"
            >
              <template slot="title">
                <i class="el-icon-info"></i> Frontend Transcript Fetching
              </template>
              This tool fetches transcripts directly in your browser using CORS proxies. Only videos with captions/subtitles enabled will work.
            </el-alert>
          </div>
        </el-card>
      </div>

      <!-- Video Info Section -->
      <div v-if="videoInfo" class="video-info-section">
        <el-card>
          <div slot="header" class="clearfix">
            <span>Video Information</span>
          </div>
          <div class="video-details">
            <div class="video-header">
              <img v-if="videoInfo.thumbnailUrl" :src="videoInfo.thumbnailUrl" alt="Video thumbnail" class="video-thumbnail" />
              <div class="video-info">
                <h3>{{ videoInfo.title }}</h3>
                <p v-if="videoInfo.author" class="video-author">by {{ videoInfo.author }}</p>
                <p v-if="videoInfo.description" class="video-description">{{ videoInfo.description.substring(0, 200) }}{{ videoInfo.description.length > 200 ? '...' : '' }}</p>
              </div>
            </div>
            <div class="video-meta">
              <el-tag v-if="videoInfo.duration" type="info">Duration: {{ videoInfo.duration }}</el-tag>
              <el-tag v-if="videoInfo.viewCount" type="success">Views: {{ formatNumber(videoInfo.viewCount) }}</el-tag>
              <el-tag v-if="transcriptStats" type="primary">Words: {{ transcriptStats.wordCount }}</el-tag>
              <el-tag v-if="transcriptStats" type="warning">Est. {{ transcriptStats.estimatedDuration }} min video</el-tag>
            </div>
          </div>
        </el-card>
      </div>

      <!-- Transcript Section -->
      <div v-if="transcript || showManualInput" class="transcript-section">
        <el-card>
          <div slot="header" class="clearfix">
            <span>Transcript</span>
            <div style="float: right;">
              <el-button v-if="!showManualInput" size="mini" type="text" @click="showManualInput = true">
                Manual Input
              </el-button>
              <el-button v-if="transcript" style="padding: 3px 0" type="text" @click="showFullTranscript = !showFullTranscript">
                {{ showFullTranscript ? 'Hide' : 'Show Full' }}
              </el-button>
            </div>
          </div>
          
          <!-- Manual Input Section -->
          <div v-if="showManualInput && !transcript" class="manual-input-section">
            <el-alert
              title="Automatic extraction failed"
              type="warning"
              :closable="false"
              show-icon
            >
              <div>
                <p>Could not automatically fetch the transcript. You can:</p>
                <ol>
                  <li>Open the video on YouTube</li>
                  <li>Click "..." menu → "Show transcript"</li>
                  <li>Copy and paste the transcript below</li>
                </ol>
              </div>
            </el-alert>
            <el-input
              v-model="manualTranscript"
              type="textarea"
              :rows="8"
              placeholder="Paste the YouTube transcript here..."
              style="margin-top: 15px;"
            ></el-input>
            <el-button 
              type="primary" 
              @click="processManualTranscript"
              :disabled="!manualTranscript"
              style="margin-top: 10px;"
            >
              Process Transcript
            </el-button>
          </div>
          
          <!-- Transcript Display -->
          <div v-else-if="transcript" class="transcript-content">
            <p v-if="!showFullTranscript" class="transcript-preview">
              {{ transcript.substring(0, 500) }}{{ transcript.length > 500 ? '...' : '' }}
            </p>
            <p v-else class="transcript-full">{{ transcript }}</p>
          </div>
        </el-card>
      </div>

      <!-- Summary Section -->
      <div v-if="summary" class="summary-section">
        <el-card>
          <div slot="header" class="clearfix">
            <span>AI Summary</span>
            <el-tag type="primary" style="float: right;">AI Generated</el-tag>
          </div>
          <div class="summary-content" v-html="formattedSummary"></div>
        </el-card>
      </div>

      <!-- Loading States -->
      <div v-if="loading" class="loading-section">
        <el-card>
          <div class="loading-content">
            <el-button loading type="primary" size="large">{{ loadingMessage }}</el-button>
          </div>
        </el-card>
      </div>

      <!-- Error Section -->
      <div v-if="error" class="error-section">
        <el-alert
          :title="error.title || 'Error'"
          type="error"
          :closable="true"
          @close="error = null"
        >
          <div>
            <p>{{ error.message }}</p>
            <div v-if="error.videoId" style="margin-top: 10px;">
              <el-button 
                type="text" 
                size="small"
                @click="openYouTubeVideo(error.videoId)"
              >
                <i class="el-icon-link"></i> Open video on YouTube
              </el-button>
              <el-button 
                type="text" 
                size="small"
                @click="showManualInput = true"
                v-if="!transcript"
              >
                <i class="el-icon-edit"></i> Manual transcript input
              </el-button>
            </div>
          </div>
        </el-alert>
      </div>
    </div>
  </div>
</template>

<script>
import { postRequestAISG } from '@/apis/front'
import { 
  extractVideoId, 
  getVideoInfo, 
  fetchTranscript,
  createSummaryPrompt,
  analyzeTranscript
} from '@/utils/youtube-simple'
import { 
  getVideoInfoWithCaptions,
  getYouTubeTranscript
} from '@/utils/youtube-iframe'

export default {
  name: 'YouTubeTranscript',
  data() {
    return {
      youtubeUrl: '',
      videoInfo: null,
      transcript: '',
      summary: '',
      loading: false,
      loadingMessage: 'Processing...',
      showFullTranscript: false,
      showManualInput: false,
      manualTranscript: '',
      error: null,
      transcriptStats: null
    }
  },
  computed: {
    formattedSummary() {
      if (!this.summary) return ''
      return this.summary
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br>')
    }
  },
  methods: {
    formatNumber(num) {
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M'
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K'
      }
      return num.toString()
    },

    async handleSubmit() {
      if (!this.youtubeUrl.trim()) {
        this.$message.warning('Please enter a YouTube URL')
        return
      }

      // Simple URL validation
      if (!this.youtubeUrl.includes('youtube.com') && !this.youtubeUrl.includes('youtu.be')) {
        this.error = {
          title: 'Invalid URL',
          message: 'Please enter a valid YouTube URL'
        }
        return
      }

      const videoId = extractVideoId(this.youtubeUrl)
      if (!videoId) {
        this.error = {
          title: 'Invalid URL',
          message: 'Could not extract video ID from the URL'
        }
        return
      }

      this.loading = true
      this.error = null
      this.transcript = ''
      this.summary = ''
      this.videoInfo = null
      this.transcriptStats = null

      try {
        await this.fetchVideoInformation(videoId)
        await this.fetchTranscript(videoId)
        if (this.transcript) {
          this.transcriptStats = analyzeTranscript(this.transcript)
          await this.generateSummary()
        }
      } catch (error) {
        console.error('Error processing video:', error)
        this.error = {
          title: 'Processing Error',
          message: error.message || 'Failed to process the video. Please try again.',
          videoId: videoId
        }
      } finally {
        this.loading = false
      }
    },

    async fetchVideoInformation(videoId) {
      this.loadingMessage = 'Fetching video information...'
      
      try {
        const videoInfo = await getVideoInfo(videoId)
        if (videoInfo) {
          this.videoInfo = {
            title: videoInfo.title,
            author: videoInfo.author,
            thumbnailUrl: videoInfo.thumbnailUrl,
            description: '',
            duration: null,
            viewCount: null
          }
        }
      } catch (error) {
        console.warn('Could not fetch video info:', error)
        // Set basic info with just the URL
        this.videoInfo = {
          title: 'YouTube Video',
          description: '',
          duration: null,
          viewCount: null
        }
      }
    },

    async fetchTranscript(videoId) {
      this.loadingMessage = 'Fetching video transcript...'
      
      try {
        // Try the new iframe API method first
        const iframeResult = await getYouTubeTranscript(videoId)
        
        if (iframeResult.transcript) {
          this.transcript = iframeResult.transcript
          console.log(`Transcript fetched using method: ${iframeResult.method}`)
          return
        }
        
        // Fallback to original method
        const transcript = await fetchTranscript(videoId)
        
        if (transcript && transcript.length > 0) {
          this.transcript = transcript
        } else {
          // Show manual input option
          this.showManualInput = true
          throw new Error('Could not fetch transcript automatically')
        }
      } catch (error) {
        // Enable manual input as fallback
        this.showManualInput = true
        throw new Error(`Automatic extraction failed. Please use manual input option.`)
      }
    },
    
    processManualTranscript() {
      if (this.manualTranscript && this.manualTranscript.trim().length > 0) {
        this.transcript = this.manualTranscript.trim()
        this.transcriptStats = analyzeTranscript(this.transcript)
        this.showManualInput = false
        
        // Generate summary for manual transcript
        this.generateSummary()
      } else {
        this.$message.warning('Please paste a valid transcript')
      }
    },

    async generateSummary() {
      this.loadingMessage = 'Generating AI summary...'
      
      const videoTitle = this.videoInfo?.title || 'YouTube Video'
      const prompt = createSummaryPrompt(videoTitle, this.transcript)

      try {
        // Try the AI summary endpoint
        const response = await postRequestAISG('generateAISummary', {
          prompt: prompt,
          content: this.transcript,
          videoTitle: videoTitle,
          maxTokens: 1500,
          temperature: 0.7
        })

        if (response && response.success && response.summary) {
          this.summary = response.summary
          return
        }

        // Fallback to basic chat completion
        const fallbackResponse = await postRequestAISG('chatCompletion', {
          messages: [
            {
              role: 'user',
              content: prompt
            }
          ],
          maxTokens: 1500,
          temperature: 0.7
        })

        if (fallbackResponse && fallbackResponse.success && fallbackResponse.message) {
          this.summary = fallbackResponse.message
          return
        }

        // If both fail, create a basic summary manually
        this.summary = this.createBasicSummary(videoTitle, this.transcript)
        
      } catch (error) {
        console.warn('AI summary failed, creating basic summary:', error)
        // Fallback to basic summary
        this.summary = this.createBasicSummary(videoTitle, this.transcript)
      }
    },

    createBasicSummary(title, transcript) {
      const stats = analyzeTranscript(transcript)
      const firstSentences = transcript.split('. ').slice(0, 3).join('. ') + '.'
      
      return `**Video Title**: ${title}

**Basic Summary**:
This video contains approximately ${stats.wordCount} words and is estimated to be about ${stats.estimatedDuration} minutes long.

**Opening Content**:
${firstSentences}

**Statistics**:
• Word Count: ${stats.wordCount}
• Estimated Duration: ${stats.estimatedDuration} minutes
• Reading Time: ${stats.readingTime} minutes

**Note**: This is a basic summary. For detailed AI analysis, please ensure your backend AI services are properly configured.`
    },
    
    openYouTubeVideo(videoId) {
      window.open(`https://www.youtube.com/watch?v=${videoId}`, '_blank')
    }
  }
}
</script>

<style scoped>
.youtube-transcript-page {
  min-height: 100vh;
  background-color: #f5f7fa;
  padding: 20px;
}

.container {
  max-width: 1000px;
  margin: 0 auto;
}

.header {
  text-align: center;
  margin-bottom: 30px;
}

.header h1 {
  color: #2c3e50;
  font-size: 2.5rem;
  margin-bottom: 10px;
  font-weight: 300;
}

.header p {
  color: #7f8c8d;
  font-size: 1.1rem;
}

.input-section,
.video-info-section,
.transcript-section,
.summary-section,
.loading-section,
.error-section {
  margin-bottom: 30px;
}

.url-examples {
  margin-top: 15px;
  font-size: 0.9rem;
  color: #7f8c8d;
}

.url-examples ul {
  margin: 5px 0;
  padding-left: 20px;
}

.video-header {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
}

.video-thumbnail {
  width: 120px;
  height: 90px;
  object-fit: cover;
  border-radius: 8px;
  flex-shrink: 0;
}

.video-info {
  flex: 1;
}

.video-details h3 {
  color: #2c3e50;
  margin-bottom: 5px;
  font-size: 1.3rem;
  line-height: 1.4;
}

.video-author {
  color: #7f8c8d;
  margin-bottom: 10px;
  font-size: 0.95rem;
}

.video-description {
  color: #5a6c7d;
  margin-bottom: 15px;
  line-height: 1.6;
}

.video-meta .el-tag {
  margin-right: 10px;
  margin-bottom: 5px;
}

.transcript-content {
  max-height: 400px;
  overflow-y: auto;
}

.transcript-preview,
.transcript-full {
  line-height: 1.8;
  color: #2c3e50;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.summary-content {
  line-height: 1.8;
  color: #2c3e50;
  font-size: 1.05rem;
}

.summary-content ::v-deep br {
  margin-bottom: 10px;
}

.loading-content {
  text-align: center;
  padding: 40px 0;
}

.clearfix:before,
.clearfix:after {
  display: table;
  content: "";
}

.clearfix:after {
  clear: both;
}

/* Responsive design */
@media (max-width: 768px) {
  .youtube-transcript-page {
    padding: 10px;
  }
  
  .header h1 {
    font-size: 2rem;
  }
  
  .container {
    max-width: 100%;
  }
}
</style>